import { useLingui } from '@lingui/react/macro'
import { Button } from 'designSystem/components/Button'
import { datapointDisplayLabel } from 'features/knowledgeDocumentViewer/utils/instrumentTagUtils'
import type { MatchedDatapoint } from 'features/timeSeries'
import { ChartLine } from 'lucide-react'
import { type FC, useCallback } from 'react'
import styled from 'styled-components'

import { Section } from './entityPanelStyles'
import { HoverTip } from './HoverTip'
import { SectionTitle } from './SectionTitle'

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`

const PlainList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
`

const SourceItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.35rem;
  font-size: 0.75rem;
  line-height: 1.35;
  color: var(--muted-foreground);
`

/** Beats GlobalStyles' layered `div { font-size: 14px }`. */
const SourceItemText = styled.div`
  min-width: 0;
  font-size: inherit;
`

const SourceDescription = styled.div`
  font-size: inherit;
  overflow-wrap: anywhere;
`

const TimeSeriesIcon = styled(ChartLine)`
  flex-shrink: 0;
  margin-top: 0.15rem;
  opacity: 0.7;
`

/** Must match the process-data tab label the Dossiers evidence panel opens. */
const PROCESS_DATA_LABEL = 'Process Data'

interface TimeSeriesSectionProps {
  datapoints: MatchedDatapoint[]
  onOpenTrend: (datapointIds: string[], label: string) => void
}

export const TimeSeriesSection: FC<TimeSeriesSectionProps> = ({
  datapoints,
  onOpenTrend,
}) => {
  const { t } = useLingui()

  const openTagTip = (datapoint: MatchedDatapoint) => {
    const piTag = datapoint.pi_tag
    return t`Open ${piTag}`
  }

  const openTimeSeries = useCallback(
    (datapoint: MatchedDatapoint) => {
      onOpenTrend([datapoint.id], datapointDisplayLabel(datapoint))
    },
    [onOpenTrend]
  )

  const openAllTimeSeries = useCallback(() => {
    if (datapoints.length === 0) return
    onOpenTrend(
      datapoints.map((datapoint) => datapoint.id),
      PROCESS_DATA_LABEL
    )
  }, [onOpenTrend, datapoints])

  const timeSeriesCount = datapoints.length

  return (
    <Section>
      <SectionHeader>
        <SectionTitle>{t`Time series`}</SectionTitle>
        {datapoints.length > 1 && (
          <HoverTip
            tip={t`Open all ${timeSeriesCount} datastreams in one Process Data tab`}>
            <Button type='button' variant='outline' onClick={openAllTimeSeries}>
              <ChartLine data-icon='inline-start' />
              {t`Open all`}
            </Button>
          </HoverTip>
        )}
      </SectionHeader>
      <PlainList>
        {datapoints.map((datapoint) => (
          <SourceItem key={datapoint.id}>
            <TimeSeriesIcon size={12} />
            <SourceItemText>
              <HoverTip tip={openTagTip(datapoint)}>
                <Button
                  type='button'
                  variant='link'
                  size='inline'
                  onClick={() => openTimeSeries(datapoint)}>
                  {datapointDisplayLabel(datapoint)}
                </Button>
              </HoverTip>
              {datapoint.description.trim() && (
                <SourceDescription>{datapoint.description}</SourceDescription>
              )}
            </SourceItemText>
          </SourceItem>
        ))}
      </PlainList>
    </Section>
  )
}
